import { useState, useEffect, useMemo } from 'react'
import {
  Text,
  Modal,
  Button,
  Heading,
  useToast,
  ModalBody,
  ButtonGroup,
  ModalFooter,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton
} from '@homebotapp/hb-react-component-catalog'
import { REA } from '../../../constants/agent_types'
import { RootState } from '../../../types/rootState'
import { useIntl, defineMessages } from 'react-intl'
import { useAppSelector } from '../../../store/hooks'
import { useDispatch, useSelector } from 'react-redux'
import { useTrackingContext } from '../../../providers/tracking'
import { sendPrequalRequestForClient } from '../../../actions/affordability'
import { selectSponsorshipFirstName } from '../../../store/selectors/sponsorship'
import { selectPreferredLoanOfficerMembership } from '../../../store/selectors/clientTeamMemberships'
import { selectHomeId } from '../../../store/selectors/home'
import history from '../../../history'
import { VerificationFlowProvider, VerificationFlowManager } from '../VerificationFlow'
import { useVerificationFlowEnabled } from '../../../hooks/useVerificationFlowEnabled'
import { useSafeVerificationFlow } from '../Questions/hooks/useSafeVerificationFlow'
import { CUSTOMER_MESSAGES } from '../../../constants/customerMessages'

export const MSG = defineMessages({
  title: {
    id: 'PrequalConfirmationModal.title',
    defaultMessage: 'Want to get pre-qualified?'
  },
  body: {
    id: 'PrequalConfirmationModal.body',
    defaultMessage:
      'Pre-qualifying with {lo} gives you a clear budget with no obligation to move forward, so you are ready to act when you find the right home.'
  },
  cancel: {
    id: 'PrequalConfirmationModal.cancel',
    defaultMessage: 'Cancel'
  },
  send: {
    id: 'PrequalConfirmationModal.confirm',
    defaultMessage: 'Confirm'
  },
  prequalRequestSuccess: {
    id: 'PrequalConfirmationModal.prequalRequestSuccess',
    defaultMessage: 'Pre-qualification request sent!'
  }
})

export interface PrequalConfirmationModalProps {
  uiContext: string
  eventSource?: string
  onClose: () => void
}

export const PrequalConfirmationModal = ({ uiContext, eventSource, onClose }: PrequalConfirmationModalProps) => {
  const intl = useIntl()
  const toast = useToast()
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()
  const sponsorName = useSelector(selectSponsorshipFirstName)
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const { startVerification, state: verificationState } = useSafeVerificationFlow()
  const isRea = useAppSelector((state: RootState) => state.customerProfile?.data?.type === REA)
  const customerName = useAppSelector((state: RootState) => state.customerProfile?.data?.firstName)
  const homeId = useSelector(selectHomeId)
  const [verificationStarted, setVerificationStarted] = useState(false)

  // Compute topic and topicKey based on LO verification status and customer type (homeowner/buyer)
  const { topicKey, topic } = useMemo(() => {
    const isLOMembershipVerified = loanOfficerMembership?.isVerified

    if (homeId) {
      // Homeowner
      return isLOMembershipVerified
        ? {
            topicKey: CUSTOMER_MESSAGES.HBN_HOMEOWNER_PREQUAL_REQUEST.topicKey,
            topic: CUSTOMER_MESSAGES.HBN_HOMEOWNER_PREQUAL_REQUEST.topic
          }
        : {
            topicKey: CUSTOMER_MESSAGES.HOMEOWNER_PREQUAL_REQUEST.topicKey,
            topic: CUSTOMER_MESSAGES.HOMEOWNER_PREQUAL_REQUEST.topic
          }
    }

    // Buyer
    return isLOMembershipVerified
      ? {
          topicKey: CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey,
          topic: CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topic
        }
      : {
          topicKey: CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topicKey,
          topic: CUSTOMER_MESSAGES.BUYER_PREQUAL_REQUEST.topic
        }
  }, [loanOfficerMembership?.isVerified, homeId])

  const handleClose = () => {
    const url = new URL(location.href)

    if (url.searchParams.has('openPrequalModal')) {
      url.searchParams.delete('openPrequalModal')
      history.push(url.pathname + url.search)
    }

    setVerificationStarted(false)
    onClose()
  }

  // When verification flow closes (returns to idle after being started), close the entire modal
  useEffect(() => {
    if (verificationStarted && verificationState.currentStep === 'idle') {
      const url = new URL(location.href)

      if (url.searchParams.has('openPrequalModal')) {
        url.searchParams.delete('openPrequalModal')
        history.push(url.pathname + url.search)
      }

      setVerificationStarted(false)
      onClose()
    }
  }, [verificationState.currentStep, verificationStarted, onClose])

  const handleClick = () => {
    // If verification is required, LO is not verified, and we're in the idle state, start verification
    if (isVerificationFlowEnabled && !loanOfficerMembership?.isVerified && verificationState.currentStep === 'idle') {
      const message = intl.formatMessage(MSG.body, {
        lo: isRea ? sponsorName : customerName
      })
      setVerificationStarted(true)
      startVerification(message, topicKey, topicKey, 'LoanOfficer', eventSource ? { event_source: eventSource } : {})
      return
    }

    dispatch(
      sendPrequalRequestForClient(trackingClient, {
        uiContext,
        customTopic: topic,
        customTopicKey: topicKey,
        eventSource
      })
    )

    toast({
      status: 'success',
      title: intl.formatMessage(MSG.prequalRequestSuccess)
    })

    handleClose()
  }

  const modalContent = (
    <Modal isOpen={!verificationStarted} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading mb={0} textAlign='left'>
            {intl.formatMessage(MSG.title)}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text as='span'>
            {intl.formatMessage(MSG.body, {
              lo: isRea ? sponsorName : customerName
            })}
          </Text>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup spacing={3}>
            <Button
              size='sm'
              tracking={{
                descriptor: 'Cancel',
                guid: 'h3mNiEcQNCjg4D7PzVq429',
                ui_context: 'PrequalConfirmationModal'
              }}
              onClick={handleClose}
            >
              {intl.formatMessage(MSG.cancel)}
            </Button>
            <Button
              size='sm'
              variant='solid'
              colorScheme='primary'
              onClick={handleClick}
              tracking={{
                descriptor: 'Confirm',
                guid: 'qzAfyt6NzijMnEcCbPvhpH',
                ui_context: 'PrequalConfirmationModal'
              }}
            >
              {intl.formatMessage(MSG.send)}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )

  if (isVerificationFlowEnabled) {
    return (
      <VerificationFlowProvider
        options={{
          enableVerificationFlow: true,
          initialPartnerType: 'LoanOfficer',
          loanOfficerMembership: loanOfficerMembership,
          includePrequal: true
        }}
      >
        <VerificationFlowManager />
        {modalContent}
      </VerificationFlowProvider>
    )
  }

  return modalContent
}
